import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Shield, Loader2 } from 'lucide-react';

/**
 * Dialog that lets a landlord (or permitted property manager) force-activate
 * a pending lease, bypassing the tenant's digital acknowledgment.
 *
 * Extracted from LandlordLeases.jsx (Refactor Step 2, Iteration 70).
 *
 * Props:
 *   open                  — boolean
 *   onOpenChange(open)    — close handler (resets reason internally via parent)
 *   reason                — current textarea value
 *   onReasonChange(value) — controlled-input change handler
 *   submitting            — boolean (true while POST is in flight)
 *   onConfirm()           — confirm handler
 */
export default function ForceActivateLeaseDialog({
  open,
  onOpenChange,
  reason,
  onReasonChange,
  submitting = false,
  onConfirm,
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-amber-600" />
            Force Activate Lease
          </DialogTitle>
          <DialogDescription>
            This will activate the lease immediately, bypassing the tenant's acknowledgment. The tenant will be notified.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
            <p className="text-sm text-amber-800 font-medium">Why force activate?</p>
            <p className="text-xs text-amber-600 mt-1">
              Common reasons: tenant is unresponsive, verbal agreement already made, move-in date has passed, tenant moved in already.
            </p>
          </div>
          <div className="space-y-2">
            <Label>Reason for Force Activation *</Label>
            <Textarea
              value={reason}
              onChange={(e) => onReasonChange(e.target.value)}
              placeholder="e.g., Tenant has moved in and verbally agreed to lease terms but hasn't acknowledged digitally..."
              rows={3}
              data-testid="force-activate-reason"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={submitting || !reason.trim()}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="confirm-force-activate-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Shield className="w-4 h-4 mr-2" />}
            Force Activate
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
